import {
  GoogleAuthProvider,
  reauthenticateWithPopup,
  reauthenticateWithRedirect,
  type User,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';
import type { PinSetting } from '@talk2nick/core';
import { generateSalt, hashPin } from '@/pin';
import { isCancelled, isMobileBrowser, isPopupBlocked } from './signIn';

/**
 * PIN 분실 시 재설정 (PLAN 5-12장).
 *
 * 복구 경로는 Google 재인증 하나뿐이다 — 계정 탈퇴와 별개로 데이터는 그대로 두고
 * PIN만 새로 쓴다. "본인 확인을 방금 했는가"는 Firebase가 토큰에 남기는 `auth_time`
 * 으로 판단한다. 재인증은 `auth_time`을 갱신하므로, 로그인 세션이 오래돼도
 * Google 비밀번호를 다시 입력해야만 통과한다.
 */

/** 이 시간 안에 인증한 세션만 PIN 재설정을 허용한다 */
const RECENT_AUTH_MS = 5 * 60 * 1000;

/** 모바일 리디렉션에서 돌아왔을 때 재설정 화면을 다시 열기 위한 표시 */
const RESET_KEY = 'talk2nick_pin_reset';
/** 사용자가 Google 화면에서 이탈한 채 방치된 경우를 정리하는 TTL */
const RESET_TTL_MS = 10 * 60 * 1000;

export type ReauthResult = 'verified' | 'redirected' | 'cancelled';

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // 사파리 프라이빗 모드 등 — 표시를 못 남기면 리디렉션 후 화면만 다시 열리지 않는다
    return null;
  }
}

function requestResetAfterRedirect(): void {
  sessionStore()?.setItem(RESET_KEY, String(Date.now()));
}

export function clearPinResetRequest(): void {
  sessionStore()?.removeItem(RESET_KEY);
}

/** 리디렉션으로 돌아온 직후인가 — LockGate가 재설정 화면을 바로 띄우는 데 쓴다 */
export function isPinResetPending(): boolean {
  const raw = sessionStore()?.getItem(RESET_KEY);
  if (raw === null || raw === undefined) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < RESET_TTL_MS;
}

/**
 * Google 재인증.
 *
 * `signInWithPopup`이 아니라 재인증을 쓴다 — 현재 로그인된 계정이 아닌 계정을 고르면
 * `auth/user-mismatch`로 실패하므로, 다른 사람이 자기 Google 계정으로 남의 PIN을
 * 재설정할 수 없다.
 */
export async function reauthenticateWithGoogle(user: User): Promise<ReauthResult> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  try {
    await reauthenticateWithPopup(user, provider);
    return 'verified';
  } catch (error) {
    if (isCancelled(error)) return 'cancelled';
    if (isPopupBlocked(error) || isMobileBrowser()) {
      requestResetAfterRedirect();
      await reauthenticateWithRedirect(user, provider);
      return 'redirected';
    }
    throw error;
  }
}

/** 최근에 본인 확인을 마친 세션인지 (ID 토큰의 auth_time 기준) */
export async function hasRecentAuth(user: User): Promise<boolean> {
  const { claims } = await user.getIdTokenResult(true);
  const authTimeSeconds = Number(claims.auth_time);
  if (!Number.isFinite(authTimeSeconds) || authTimeSeconds <= 0) return false;
  return Date.now() - authTimeSeconds * 1000 < RECENT_AUTH_MS;
}

/**
 * 새 PIN 저장.
 * `users/{uid}.settings`의 나머지 값은 건드리지 않도록 점(.) 경로로 갱신한다.
 */
export async function updatePin(user: User, pin: string): Promise<void> {
  const salt = generateSalt();
  const hash = await hashPin(pin, salt);
  const pinSetting: PinSetting = { hash, salt, updatedAt: Date.now() };
  await updateDoc(doc(getDb(), 'users', user.uid), { 'settings.pin': pinSetting });
}
